import React, { JSX } from "react";
import { ImageWithSpinner } from "./ImageWithSpinner";
import { useSelector } from "react-redux";
import { documentPagesState, documentPageState, selectedNavPosState } from "../../../redux/slices/statusesSlice";
import { PdfWithSpinner } from "./PdfWithSpinner";
import { ReduxUpdater } from "../../../redux/ReduxUpdater";
import PageUp from "../icons/PageUp";
import PageDown from "../icons/PageDown";
import { Download } from "lucide-react";
import Tooltip from "../tooltiped/Tooltip";
import { selectedUserState } from "../../../redux/slices/userSlice";

interface ImageWithSpinnerProps {
    allowDownload?: boolean;
    allowUpload?: boolean;
    src: string;
    alt: string;
    description: string | JSX.Element | null;
    rounded?: boolean;
    className?: string;
    cc?: string;
    type: 'image' | 'pdf';
}

export const ReceiptPreview: React.FC<ImageWithSpinnerProps> = ({
    allowDownload = false,
    allowUpload = false,
    src,
    alt,
    description,
    rounded = true,
    className = undefined,
    cc = undefined,
    type,
}) => {
    const currentSelectedUserState = useSelector(selectedUserState);
    const selectedNavPosition = useSelector(selectedNavPosState);
    const documentPage = useSelector(documentPageState);
    const documentPages = useSelector(documentPagesState);
    
    const [rotationAngle, setRotationAngle] = React.useState(0);
    
    React.useEffect(() => {
        setRotationAngle(0);
    }, [selectedNavPosition]);

    const AddReceiptButton = () => {
        if (!allowUpload) {
            return null;
        }

        return (
            <div
                className="relative w-full h-0"
                style={{
                    zIndex: 20,
                }}
            >
                <Tooltip
                    className={`items-center justify-center`}
                    text="Upload Receipt"
                    placement="top-start"
                    textStyling='z-10 max-w-[350px] whitespace-break-spaces'
                >
                    <button
                        className="m-0 px-2 py-0 bg-buttons-onState text-white rounded-tl-md rounded-br-xl text-2xl"
                        onClick={() => {
                            ReduxUpdater.currentDialog = 'uploadReceipt';
                        }}
                    >
                        +
                    </button>
                </Tooltip>
            </div>
        );
    }

    const PaidWithCard = () => {
        if (!cc) {
            return null;
        }
        return (
            <div
                className="relative w-full h-0 z-10"
            >
                <span
                    className="py-0.25 px-1.5 bg-buttons-onState text-white rounded-xl text-sm float-right"
                    style={{
                        top: '-28px',
                        right: '16px',
                        position: 'relative'
                    }}
                >
                    {currentSelectedUserState && currentSelectedUserState.debit_card_number && cc === currentSelectedUserState?.debit_card_number.slice(1)
                        ? `Boat Card`
                        : `Card: x${cc}`
                    }
                </span>
            </div>
        );
    };

    if (type === 'pdf') {
        return (
            <div className={`w-full h-[100%] bg-bg-banner-light border border-1 border-border-default ${rounded ? 'rounded-xl' : ''} inline-flex flex-col justify-start items-center overflow-hidden ${className}`}>
                <AddReceiptButton />
                <PdfWithSpinner
                    src={src}
                    alt={alt}
                    rotation={rotationAngle}
                    wrapperClassName={`w-full h-full aspect-video ${rounded ? 'rounded-xl' : ''}`}
                    className="w-full h-full object-contain"
                />
                <PaidWithCard />
                {description !== null && <div className="z-10 self-stretch h-10 py-3.5 bg-gray-100 flex flex-col justify-center items-center gap-2.5">
                    <div
                        className="sm:w-auto w-[500px] h-4 text-center justify-start text-editable-field text-xs font-normal font-['Roboto'] leading-none tracking-wide"
                    >
                        {description}
                    </div>
                </div>}
                <div className="z-10 self-stretch h-12 bg-gray-100 gap-2.5 grid grid-cols-3">
                    <div className="col-span-1">&nbsp;</div>
                    <div className="col-span-1 justify-center items-center inline-flex gap-2">
                        {documentPages > 1 && <button
                            className="RotateCcw w-6"
                            disabled={documentPage === 1}
                            onClick={() => {
                                ReduxUpdater.documentPage = documentPage - 1;
                            }}
                        >
                            <PageUp
                                {...{color: documentPage === 1 ? 'gray' : 'black'}}
                                height={18}
                                width={24}
                            />
                        </button>}
                        <div className="w-12 h-7 bg-buttons-onState rounded-lg overflow-hidden">
                            <div className="text-lg text-center justify-center text-white font-semibold font-['Inter']">PDF</div>
                        </div>
                        {documentPages > 1 && <button
                            className="RotateCw center"
                            disabled={documentPage === documentPages}
                            onClick={() => {
                                ReduxUpdater.documentPage = documentPage + 1;
                            }}
                        >
                            <PageDown
                                {...{color: documentPage === documentPages ? 'gray' : 'black'}}
                                height={18}
                                width={24}
                            />
                        </button>}
                    </div>
                    <div className="col-span-1 justify-end items-center inline-flex gap-2 pr-4">
                        {allowDownload &&
                            <a
                                className="RotateCw"
                                href={src}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <Download
                                    {...{color: 'black'}}
                                    height={24}
                                    width={24}
                                />
                            </a>
                        }
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`w-full h-[100%] bg-bg-banner-light border border-1 border-border-default ${rounded ? 'rounded-xl' : ''} inline-flex flex-col justify-start items-center overflow-hidden ${className}`}>
            <AddReceiptButton />
            <ImageWithSpinner
                src={src}
                alt={alt}
                rotation={rotationAngle}
                wrapperClassName={`w-full h-full aspect-video ${rounded ? 'rounded-xl' : ''}`}
                className="w-full h-full object-contain"
            />
            <PaidWithCard />
            <div className="z-10 self-stretch h-10 py-3.5 bg-gray-100 flex flex-col justify-center items-center gap-2.5">
                <div
                    className="sm:w-auto w-[500px] h-4 text-center justify-start text-editable-field text-xs font-normal font-['Roboto'] leading-none tracking-wide"
                >
                    {description}
                </div>
            </div>
            <div className="z-10 self-stretch h-12 bg-gray-100 gap-2.5 grid grid-cols-3">
                <div className="col-span-1">&nbsp;</div>
                <div className="col-span-1 justify-center items-center inline-flex gap-2">
                    <button
                        className="RotateCcw relative"
                        onClick={() => {
                            setRotationAngle((rotationAngle + 270) % 360);
                        }}
                    >
                        <svg width="33" height="33" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g clipPath="url(#clip0_319_1229)">
                                <path d="M1.40381 5.37607V13.3761M1.40381 13.3761H9.40381M1.40381 13.3761L7.59048 7.56274C9.43169 5.72463 11.8204 4.5347 14.3967 4.17223C16.973 3.80975 19.5973 4.29438 21.8743 5.55309C24.1512 6.8118 25.9574 8.7764 27.0206 11.1509C28.0839 13.5253 28.3467 16.1811 27.7694 18.7179C27.1921 21.2547 25.806 23.5352 23.82 25.2157C21.8339 26.8963 19.3555 27.8859 16.7581 28.0354C14.1607 28.1849 11.5851 27.4862 9.41935 26.0446C7.25359 24.603 5.615 22.4966 4.75048 20.0427" stroke="var(--color-brand-icon-dark, #718096)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                            </g>
                            <defs>
                                <clipPath id="clip0_319_1229">
                                    <rect width="32" height="32" fill="white" transform="translate(0.0703125 0.0429688)" />
                                </clipPath>
                            </defs>
                        </svg>
                    </button>
                    <button
                        className="RotateCw relative"
                        onClick={() => {
                            setRotationAngle((rotationAngle + 90) % 360);
                        }}
                    >
                        <svg width="33" height="33" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M30.737 5.37616V13.3762M30.737 13.3762H22.737M30.737 13.3762L24.5637 7.56282C22.7245 5.72248 20.337 4.52974 17.7609 4.16435C15.1849 3.79896 12.5599 4.28071 10.2815 5.53701C8.00303 6.79331 6.19465 8.75609 5.1288 11.1296C4.06295 13.5031 3.79739 16.1587 4.37214 18.6962C4.94688 21.2338 6.33079 23.5158 8.31532 25.1984C10.2999 26.881 12.7775 27.873 15.3749 28.0249C17.9723 28.1769 20.5487 27.4805 22.7159 26.0408C24.883 24.6011 26.5236 22.496 27.3903 20.0428" stroke="var(--color-brand-icon-dark, #718096)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                </div>
                <div className="col-span-1 justify-end items-center inline-flex gap-2 pr-4">
                    {allowDownload &&
                        <a
                            className="RotateCw"
                            href={src}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <Download
                                {...{color: 'black'}}
                                height={24}
                                width={24}
                            />
                        </a>
                    }
                </div>
            </div>
        </div>
    );
};
